/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import EditIcon from '@material-ui/icons/Edit';
import { useTranslation } from 'react-i18next';
import IconButton from '@material-ui/core/IconButton';
import { Link as ScrollLink } from 'react-scroll';
import { omit } from 'lodash';

import { scrollerSettings } from '../../../../../settings';
import Tooltip from '../../../Tooltip';
import { CardContext } from '../../CardContext';
import { selectVersion } from '../../../../../redux/features/configuration/configuration.slice';
import { dataTestId } from '../../../../../shared/utils/versionUtils';
import { checkTitleAndRemoveWhitespaces } from '../../../../../shared/utils/stringUtils';
import { OperationType } from '../../../../../shared/enums/OperationType';
import { PartyStatus } from '../../../../addressBook/PartyStatus';

export default function PartyEntry({ entry, readOnly }) {
  const { t } = useTranslation();
  const { classes, sectionName, submission, onSectionChange } = useContext(CardContext);
  const [seeLess, setSeeLess] = useState(false);
  const [visibleItems, setVisibleItems] = useState([]);
  const selectedVersion = useSelector(selectVersion);
  // TODO: harmonize this with frontendCustomissation.json -> summary.card.initiallyVisibleItemsSize
  const initialVisibleItems = entry.initialVisibleItems || 2;
  const allItems = entry.extra ? entry.items.concat(entry.extra) : entry.items;
  const initialItems = allItems.slice(0, initialVisibleItems);

  const handleEditItem = (id, parentType, sectionType) => () => {
    if (submission) {
      const newSubmission = {
        ...submission,
        [parentType]: {
          ...submission[parentType],
          [sectionType]: submission[parentType][sectionType]?.map(p => (p.id === id ? omit(p, 'state') : p)),
        },
      };
      onSectionChange({
        formData: newSubmission,
        sectionType,
        changeProps: {
          sectionOperation: OperationType.EDIT,
          sectionItemId: id,
          sectionName,
        },
      });
    }
  };

  const calculateScrollId = (entryItem, parentType, sectionType) => {
    if (readOnly || !submission) {
      return null;
    }
    const elementIndex = submission[parentType][sectionType]?.findIndex(
      item => item.id === entryItem.id && item.state !== PartyStatus.CONFIRMED,
    );
    if (elementIndex >= 0) {
      return `${sectionName}_${entry.sectionType}_${elementIndex}-title`;
    }
    return `${sectionName}_${entry.sectionType}`;
  };

  const handleShowAllItems = () => {
    setVisibleItems(allItems);
    setSeeLess(true);
  };

  const handleShowLess = () => {
    setVisibleItems(initialItems);
    setSeeLess(false);
  };

  useEffect(() => {
    setVisibleItems(seeLess ? allItems : initialItems);
  }, [entry]);

  return (
    <>
      <Grid item container spacing={2} className={classes.multiEntry}>
        {entry.sectionHeader && (
          <Grid item xs={12}>
            <Typography variant="h6" component="span">
              {' '}
              {entry.sectionHeader}
            </Typography>
          </Grid>
        )}
        {visibleItems &&
          visibleItems.length > 0 &&
          visibleItems.map((entryItem, index) => {
            const summaryCardTitle = checkTitleAndRemoveWhitespaces(entryItem.title);
            return (
              <Grid item key={index} container spacing={2} className={classes.content}>
                {entryItem.icon && (
                  <Grid item xs={1} className={classes.entryIcon}>
                    <entryItem.icon />
                  </Grid>
                )}
                {entryItem.title && (
                  <Grid item xs>
                    <Typography
                      variant="body2"
                      display="inline"
                      className={classes.partyEntryKey}
                      {...dataTestId({
                        value: `${sectionName}_summary_${summaryCardTitle}-title--${selectedVersion.value}`,
                      })}
                    >
                      {entryItem.title}
                    </Typography>
                  </Grid>
                )}
                {entryItem.value && (
                  <Grid item xs={5} className={classes.entryValueGridItem}>
                    <Tooltip
                      title={entryItem.value}
                      placement="top"
                      arrow
                      disableHoverListener={entryItem.value.length < 20}
                    >
                      <>
                        <Typography
                          variant="body2"
                          noWrap
                          display="inline"
                          className={classes.partyEntryValue}
                          {...dataTestId({
                            value: `${sectionName}_summary_${summaryCardTitle}-value--${selectedVersion.value}`,
                          })}
                        >
                          {entryItem.value}
                        </Typography>
                      </>
                    </Tooltip>
                  </Grid>
                )}
                {!entry?.hideLinkTo && (
                  <Grid item xs={1} className={classes.entryIcon}>
                    <ScrollLink
                      disabled={readOnly}
                      activeClass="active"
                      to={calculateScrollId(entryItem, entry.type, entry.sectionType)}
                      spy={true}
                      smooth={scrollerSettings.smooth}
                      offset={scrollerSettings.offset}
                      delay={scrollerSettings.delay}
                      duration={scrollerSettings.duration}
                    >
                      <IconButton
                        disabled={readOnly}
                        onClick={handleEditItem(entryItem.id, entry.type, entry.sectionType)}
                        seql-scroll-link-to={readOnly ? null : sectionName.concat('_').concat(entry.sectionType)}
                      >
                        <EditIcon />
                      </IconButton>
                    </ScrollLink>
                  </Grid>
                )}
              </Grid>
            );
          })}

        {allItems.length > initialVisibleItems && !seeLess && (
          <Grid item container spacing={2} className={classes.content}>
            <Grid item>
              <Typography variant="body2" display="inline" className={classes.seeMoreBox}>
                {'+'.concat(`${allItems.length - initialItems.length}`)}
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography
                variant="body2"
                noWrap
                display="inline"
                className={classes.seeAll}
                onClick={handleShowAllItems}
              >
                {entry.extraText}
              </Typography>
            </Grid>
          </Grid>
        )}

        {seeLess && (
          <Grid item container spacing={2} className={classes.content}>
            <Grid item xs={12}>
              <Typography variant="body2" noWrap display="inline" className={classes.seeAll} onClick={handleShowLess}>
                {t('summary.parties.see.less')}
              </Typography>
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );
}
